import { usePeriod } from '@/context/PeriodContext';
import { Box, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type DateFieldValueParams = {
  initPeriod: dayjs.Dayjs | null;
  setInitPeriod: Dispatch<SetStateAction<dayjs.Dayjs | null>>;
  endPeriod: dayjs.Dayjs | null;
  setEndPeriod: Dispatch<SetStateAction<dayjs.Dayjs | null>>;
};

export default function PrevisionPeriod(props: DateFieldValueParams) {
  const { initPeriod, setInitPeriod, endPeriod, setEndPeriod } = props;
  const { i18n, t } = useTranslation('periodDetails');
  const { selectedPeriod: initialDate } = usePeriod();

  const minDate = dayjs().subtract(5, 'year').startOf('year');

  const handleInitDateChange = (newValue: dayjs.Dayjs | null) => {
    if (newValue) {
      const startMonth = newValue.startOf('month');
      setInitPeriod(startMonth);

      if (!endPeriod || endPeriod.isBefore(startMonth)) {
        setEndPeriod(startMonth);
      }
    }
  };

  const handleEndDateChange = (newValue: dayjs.Dayjs | null) => {
    if (newValue && initPeriod) {
      const endMonth = newValue.startOf('month');

      if (endMonth.isAfter(initPeriod) || endMonth.isSame(initPeriod, 'month')) {
        setEndPeriod(endMonth);
      }
    }
  };

  useEffect(() => {
    if (initialDate?.date) {
      const initData = dayjs().startOf('year');
      const endData = dayjs();
      setInitPeriod(initData);
      setEndPeriod(endData);
    }
  }, [initialDate, setInitPeriod, setEndPeriod]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '60%' }}>
        <DatePicker
          views={['year', 'month']}
          openTo="month"
          value={initPeriod}
          onChange={handleInitDateChange}
          sx={{ width: '190px', fontSize: '8px' }}
          minDate={minDate}
          defaultCalendarMonth={minDate}
          slotProps={{ textField: { size: 'small' } }}
        />

        <Typography sx={{ margin: '0 8px' }}>{t('common:at')}</Typography>

        <DatePicker
          views={['year', 'month']}
          openTo="month"
          value={endPeriod}
          onChange={handleEndDateChange}
          sx={{ width: '190px', fontSize: '8px' }}
          minDate={initPeriod || minDate}
          defaultCalendarMonth={minDate}
          slotProps={{ textField: { size: 'small' } }}
        />
      </Box>
    </LocalizationProvider>
  );
}
